import { BookOpen, ChevronDown, ChevronRight, FileText, Plus, Search, X } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type JSX } from "react";

import type { WikiPageSummaryDTO } from "../../../common/api/types.js";
import { PrimaryButton } from "../../../common/components/PrimaryButton.js";
import { useModalFocus } from "../../../common/components/useModalFocus.js";
import type { ModulePageProps } from "../../ModuleDefinition.js";
import { useCreateWikiPage, useWikiPages } from "../query/wikiQueries.js";
import { useWikiLocation } from "../wikiLocation.js";
import { buildWikiTree, type WikiTreeNode } from "../wikiTree.js";

export function WikiSidebar({ me }: ModulePageProps): JSX.Element {
  const location = useWikiLocation();
  const allPages = useWikiPages();
  const create = useCreateWikiPage();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [createParentId, setCreateParentId] = useState<string | null | undefined>(undefined);
  const canEdit = me.role !== "GUEST";

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const searchResults = useWikiPages(search);
  const pages = allPages.data ?? [];
  const tree = useMemo(() => buildWikiTree(pages), [pages]);

  return (
    <div className="flex min-h-full flex-col gap-3">
      <div className="flex items-center justify-between px-1">
        <div><h2 className="text-sm font-bold text-ink">Wissensbereich</h2><p className="text-xs text-ink-muted">Seiten und Unterseiten</p></div>
        {canEdit ? <button type="button" aria-label="Seite anlegen" onClick={() => setCreateParentId(null)} className="grid h-9 w-9 place-items-center rounded-full bg-brand/10 text-brand hover:bg-brand/15"><Plus className="h-4 w-4" /></button> : null}
      </div>

      <label className="relative block">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-ink-muted" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Wiki durchsuchen …" className="w-full rounded-xl border border-line bg-surface-sunken py-2 pl-9 pr-9 text-sm text-ink outline-none focus:border-brand" />
        {query ? <button type="button" onClick={() => setQuery("")} aria-label="Suche leeren" className="absolute right-2 top-1.5 p-1 text-ink-muted"><X className="h-4 w-4" /></button> : null}
      </label>

      {(allPages.error ?? searchResults.error) ? <p role="alert" className="px-2 text-sm text-danger">{(allPages.error ?? searchResults.error)?.message}</p> : null}
      {allPages.isPending ? <p className="px-2 text-sm text-ink-muted">Seiten werden geladen …</p> : null}

      <nav className="min-h-0 space-y-0.5">
        {search ? (
          <SearchResults pages={searchResults.data ?? []} selectedId={location.pageId} onOpen={location.openPage} />
        ) : tree.map((node) => (
          <TreeEntry key={node.id} node={node} depth={0} selectedId={location.pageId} canEdit={canEdit} onOpen={location.openPage} onAddChild={(parentId) => setCreateParentId(parentId)} />
        ))}
      </nav>

      {!allPages.isPending && pages.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-5 text-center">
          <BookOpen className="mx-auto h-6 w-6 text-ink-muted" />
          <p className="mt-2 text-sm font-semibold text-ink">Noch keine Seiten</p>
          <p className="mt-1 text-xs text-ink-muted">Lege die erste Seite für euer Wissen an.</p>
          {canEdit ? <button type="button" onClick={() => setCreateParentId(null)} className="mt-3 text-sm font-semibold text-brand">Erste Seite anlegen</button> : null}
        </div>
      ) : null}

      {createParentId !== undefined ? (
        <CreatePageDialog
          pages={pages}
          initialParentId={createParentId}
          busy={create.isPending}
          error={create.error?.message ?? null}
          onClose={() => setCreateParentId(undefined)}
          onCreate={(title, parentId) => create.mutate({ title, ...(parentId ? { parentId } : {}) }, {
            onSuccess: (page) => { setCreateParentId(undefined); location.openPage(page.id); },
          })}
        />
      ) : null}
    </div>
  );
}

function TreeEntry({ node, depth, selectedId, canEdit, onOpen, onAddChild }: {
  node: WikiTreeNode;
  depth: number;
  selectedId: string | null;
  canEdit: boolean;
  onOpen: (pageId: string) => void;
  onAddChild: (pageId: string) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  return (
    <div>
      <div className={["group flex items-center rounded-lg pr-1", selectedId === node.id ? "bg-brand/10 text-brand" : "text-ink hover:bg-surface-sunken"].join(" ")} style={{ paddingLeft: `${Math.min(depth, 6) * 0.75}rem` }}>
        <button type="button" onClick={() => setExpanded((current) => !current)} disabled={!hasChildren} aria-label={expanded ? "Unterseiten einklappen" : "Unterseiten ausklappen"} className="grid h-8 w-6 shrink-0 place-items-center text-ink-muted disabled:opacity-0">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <button type="button" onClick={() => onOpen(node.id)} className="flex min-w-0 flex-1 items-center gap-2 py-2 text-left">
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate text-sm">{node.title}</span>
        </button>
        {canEdit ? <button type="button" onClick={() => onAddChild(node.id)} aria-label={`Unterseite zu ${node.title} anlegen`} className="rounded-md p-1.5 text-ink-muted hover:bg-surface lg:opacity-0 lg:group-hover:opacity-100 lg:focus:opacity-100"><Plus className="h-3.5 w-3.5" /></button> : null}
      </div>
      {expanded ? node.children.map((child) => <TreeEntry key={child.id} node={child} depth={depth + 1} selectedId={selectedId} canEdit={canEdit} onOpen={onOpen} onAddChild={onAddChild} />) : null}
    </div>
  );
}

function SearchResults({ pages, selectedId, onOpen }: { pages: WikiPageSummaryDTO[]; selectedId: string | null; onOpen: (id: string) => void }): JSX.Element {
  if (pages.length === 0) return <p className="px-2 py-4 text-center text-sm text-ink-muted">Keine Seite gefunden.</p>;
  return <>{pages.map((page) => <button key={page.id} type="button" onClick={() => onOpen(page.id)} className={["flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left", selectedId === page.id ? "bg-brand/10 text-brand" : "text-ink hover:bg-surface-sunken"].join(" ")}><FileText className="h-4 w-4 shrink-0" /><span className="min-w-0"><span className="block truncate text-sm font-medium">{page.title}</span><span className="block truncate text-xs text-ink-muted">Geändert von {page.updatedByDisplayName}</span></span></button>)}</>;
}

function CreatePageDialog({ pages, initialParentId, busy, error, onClose, onCreate }: {
  pages: WikiPageSummaryDTO[];
  initialParentId: string | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (title: string, parentId: string | null) => void;
}): JSX.Element {
  const panel = useModalFocus<HTMLFormElement>(onClose);
  const [title, setTitle] = useState("");
  const [parentId, setParentId] = useState(initialParentId ?? "");
  const submit = (event: FormEvent): void => { event.preventDefault(); onCreate(title, parentId || null); };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-6">
      <button type="button" aria-label="Dialog schließen" onClick={onClose} className="absolute inset-0" />
      <form ref={panel} role="dialog" aria-modal="true" tabIndex={-1} onSubmit={submit} className="relative w-full max-w-md rounded-t-2xl bg-surface-raised p-5 shadow-2xl sm:rounded-2xl">
        <div className="mb-4 flex items-center"><h2 className="text-lg font-bold text-ink">Neue Wiki-Seite</h2><button type="button" onClick={onClose} className="ml-auto p-2 text-ink-muted"><X className="h-5 w-5" /></button></div>
        <label className="block text-sm font-medium text-ink">Titel<input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2" /></label>
        <label className="mt-3 block text-sm font-medium text-ink">Übergeordnete Seite<select value={parentId} onChange={(event) => setParentId(event.target.value)} className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2"><option value="">Oberste Ebene</option>{pages.map((page) => <option key={page.id} value={page.id}>{page.title}</option>)}</select></label>
        {error ? <p role="alert" className="mt-3 text-sm text-danger">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={onClose} className="px-3 py-2 text-sm text-ink-muted">Abbrechen</button><PrimaryButton disabled={busy || !title.trim()}>{busy ? "Wird angelegt …" : "Seite anlegen"}</PrimaryButton></div>
      </form>
    </div>
  );
}
